import { useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { tokenStorage } from '@/lib/tokenStorage';
import { getGetSdkTokenQueryKey } from '@/api/generated/endpoints/dashboard';
import { useBoundStore } from '@/store';

/** localStorage key used to broadcast logout events across tabs */
const LOGOUT_STORAGE_KEY = 'logout-event';

/**
 * Provides a logout function that clears all session state and redirects to the login page.
 *
 * Cleanup sequence:
 * 1. Clear ThoughtSpot SDK token from sessionStorage
 * 2. Remove SDK token query from React Query cache
 * 3. Reset ThoughtSpot Zustand store (selected region, auth status, etc.)
 * 4. Clear persisted store data from localStorage
 * 5. Redirect to Auth0 logout (navigates away from the app)
 *
 * Cross-tab sync:
 * - On logout, writes to localStorage which fires a `storage` event on other tabs
 * - Other tabs listen for this event and perform the same cleanup
 * - The `storage` event only fires on OTHER tabs (browser spec), so there's no loop
 *
 * @returns A function that broadcasts logout to all tabs and performs local cleanup
 */
export function useLogout() {
  const { logout } = useAuth0();
  const queryClient = useQueryClient();
  const resetThoughtspotStore = useBoundStore((state) => state.reset);

  /** Performs the full cleanup sequence without broadcasting to other tabs */
  const performLogout = useCallback(async () => {
    tokenStorage.clearToken();
    queryClient.removeQueries({ queryKey: getGetSdkTokenQueryKey() });
    resetThoughtspotStore();
    localStorage.removeItem('dashboard-store');
    await logout({ logoutParams: { returnTo: import.meta.env.VITE_AUTH0_LOGIN_SPA_URL } });
  }, [logout, queryClient, resetThoughtspotStore]);

  // Cross-tab sync — when another tab logs out, the storage event fires here
  // and we perform the same cleanup locally
  useEffect(() => {
    const handleLogoutBroadcast = (e: StorageEvent) => {
      if (e.key === LOGOUT_STORAGE_KEY) performLogout();
    };
    window.addEventListener('storage', handleLogoutBroadcast);
    return () => window.removeEventListener('storage', handleLogoutBroadcast);
  }, [performLogout]);

  /** Broadcasts logout to other tabs, then performs local cleanup */
  const handleLogout = useCallback(async () => {
    localStorage.setItem(LOGOUT_STORAGE_KEY, Date.now().toString());
    await performLogout();
  }, [performLogout]);

  return handleLogout;
}
